import React, { useState, useMemo } from "react";
import { connect } from "react-redux";

import { bindActionCreators, Dispatch } from "redux";

import { FilterSection, FilterItem, FilterItemField, FilterTextInput, Dropdown, Checkbox } from "@plex/react-components";

import { Tenants } from "config";
import { ILocalizable, commonMessages } from "infrastructure/localization";
import { useLocalizedValues, useLocalizer, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { messagesActions } from "../Messages.actions";
import { IMessagesState, IMessagesSearchQuery } from "../Messages.state";

interface IMessagesFilterProps {
  statuses: ILocalizable[];
  parents: ILocalizable[];
  searchMessages: Function;
}

const MessagesFilter: React.FC<IMessagesFilterProps> = ({ statuses, parents, searchMessages }) => {
  const localize = useLocalizer();
  const userContext = useUserContext();

  const localizedValues = useLocalizedValues(() => ({
    messageIdProperty: displayMessages.property("messageId"),
    textProperty: displayMessages.property("text"),
    parentProperty: displayMessages.property("parent"),
    statusProperty: displayMessages.property("status"),
    displayTokensProperty: displayMessages.property("displayTokens"),
    all: commonMessages.property("all")
  }));

  const [state, setState] = useState<IMessagesSearchQuery>({
    messageId: "",
    text: "",
    selectedParent: undefined,
    displayTokens: false,
    selectedStatus: undefined
  });

  const [pinned, setPinned] = useState(false);

  const filteredStatuses = useMemo(() => {
    const inUseStatus = displayMessages.option("messageStatusInUse").value;
    const deprecatedStatus = displayMessages.option("messageStatusDeprecated").value;
    return userContext.hasTenantAccess(Tenants.Plex) ? statuses : statuses.filter(s => s.messageId.value === inUseStatus || s.messageId.value === deprecatedStatus);
  }, [statuses, userContext]);

  const onFilterChange = <T, P extends keyof IMessagesSearchQuery>(value: T, prop: P) => {
    setState({ ...state, [prop]: value });
  };

  const onCheckboxChange = e => {
    setState({
      ...state,
      [e.currentTarget.id]: e.currentTarget.checked
    });
  };

  const onFilterSelect = <T, P extends keyof IMessagesSearchQuery>(rows: T[], name: P) => {
    const newState = {
      ...state,
      [name]: rows[0]
    };

    setState(newState);
  };

  const getSelectedItems = <T,>(value: T): Array<NonNullable<T>> => (value ? [value as NonNullable<T>] : []);

  return (
    <FilterSection
      pinned={pinned}
      hasDefaults={false}
      onSearch={() => {
        searchMessages({ ...state });
      }}
      onPin={setPinned}
    >
      <FilterItem labelText={localizedValues.messageIdProperty}>
        <FilterItemField shouldCollapse={() => !state.messageId} value={state.messageId}>
          <FilterTextInput value={state.messageId} onChange={e => onFilterChange(e.currentTarget.value, "messageId")} />
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.textProperty}>
        <FilterItemField value={state.text} shouldCollapse={() => !state.text}>
          <FilterTextInput value={state.text} onChange={e => onFilterChange(e.currentTarget.value, "text")} />
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.parentProperty}>
        <FilterItemField shouldCollapse={() => !state.selectedParent}>
          <Dropdown
            placeholder={localizedValues.all}
            includeEmpty
            items={parents}
            keySelector={item => (item as ILocalizable).messageId.value}
            displaySelector={item => localize((item as ILocalizable).messageId)}
            selected={getSelectedItems(state.selectedParent)}
            onSelectionChanged={items => onFilterSelect(items, "selectedParent")}
          />
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.statusProperty}>
        <FilterItemField shouldCollapse={() => !state.selectedStatus}>
          <Dropdown
            items={filteredStatuses}
            placeholder={localizedValues.all}
            includeEmpty
            keySelector={item => (item as ILocalizable).messageId.value}
            displaySelector={item => localize((item as ILocalizable).messageId)}
            selected={getSelectedItems(state.selectedStatus)}
            onSelectionChanged={items => onFilterSelect(items, "selectedStatus")}
          />
        </FilterItemField>
      </FilterItem>

      <FilterItem labelText={localizedValues.displayTokensProperty}>
        <FilterItemField shouldCollapse={() => !state.displayTokens}>
          <Checkbox id="displayTokens" checked={state.displayTokens} onChange={onCheckboxChange} />
        </FilterItemField>
      </FilterItem>
    </FilterSection>
  );
};

export default connect(
  (state: { messages: IMessagesState }) => {
    return {
      statuses: [...state.messages.filter.statuses],
      parents: [...state.messages.filter.parents]
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        searchMessages: messagesActions.searchMessages
      },
      dispatch
    );
  }
)(MessagesFilter);
